import { CircularProgress, Grid, TextField } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zipCodeRegex } from '../utils/regex';
import Lock from '@material-ui/icons/Lock';
import CustomButton from './CustomButton';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const ClipForm = ({ processing, clipApiKey, handleCancel, handleCardTokenCallback }) => {
  const { t } = useTranslation('general');

  const [card, setCard] = useState(null);
  const [clipSdk, setClipSdk] = useState(null);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    importScript();
    return detachScript;
  }, []);

  useEffect(() => {
    if (clipSdk !== null) displayCardForm();
  }, [clipSdk]);

  const importScript = () => {
    const scriptImported = document.getElementById('clip-sdk') !== null;
    if (!scriptImported) {
      const script = document.createElement('script');
      script.crossOrigin = '';
      script.src = 'https://sdk.clip.mx/js/clip-sdk.js';
      script.id = 'clip-sdk';
      document.body.appendChild(script);
      script.onload = initialiazeSdk;
    }
  };

  const initialiazeSdk = () => {
    const API_KEY = clipApiKey;
    const currentSdk = new window.ClipSDK(API_KEY);
    setClipSdk(currentSdk);
  };

  const detachScript = () => {
    const sdkElement = document.getElementById('clip-sdk');
    if (sdkElement && sdkElement !== null) sdkElement.remove();
  };

  const displayCardForm = () => {
    const checkoutContainer = document.getElementById('checkout');
    if (checkoutContainer !== null) {
      checkoutContainer.innerHTML = '';
      const currentCard = clipSdk.element.create('Card', {
        theme: 'light',
        locale: 'es',
      });
      currentCard.mount('checkout');
      setCard(currentCard);
    }
  };

  const handleClipTokenizationError = (error) => {
    switch (error.code) {
      case 'CL2200':
      case 'CL2290':
        alert('Error: ' + error.message);
        throw error;
      case 'AI1300':
        console.log('Error: ', error.message);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let cardToken = null;
    setDisabled(true);
    const valid = await formik.validateForm(formik.values);
    if (Object.keys(valid).length > 0) {
      return setDisabled(false);
    }
    const address = formik.values;
    try {
      // Obtén el token de la tarjeta
      cardToken = await card.cardToken();
      const cardTokenID = cardToken.id;
      setDisabled(false);
      const preventionData = await card.preventionData();
      handleCardTokenCallback(cardTokenID, preventionData, address);
    } catch (error) {
      // Maneja errores durante la tokenización de la tarjeta
      handleClipTokenizationError(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      street: '',
      number: '',
      zip_code: '',
    },
    validationSchema: Yup.object().shape({
      street: Yup.string().required(t('Required Field')),
      number: Yup.string().required(t('Required Field')),
      zip_code: Yup.string()
        .matches(zipCodeRegex, { message: 'Código postal inválido' })
        .required(t('Required Field')),
    }),
    onSubmit: handleSubmit,
  });

  return (
    <div>
      <form id="payment-form" onSubmit={formik.handleSubmit}>
        <p>Dirección de Facturación</p>
        <br />
        <Grid container spacing={1} className="mb-3">
          <Grid item xs={12} md={6}>
            <TextField
              variant="outlined"
              name="street"
              label="Calle"
              className="w-full"
              error={!!formik.errors.street}
              helperText={formik.errors.street}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              variant="outlined"
              name="number"
              label="Número"
              className="w-full"
              error={!!formik.errors.number}
              helperText={formik.errors.number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              variant="outlined"
              name="zip_code"
              label="Código postal"
              className="w-full"
              error={!!formik.errors.zip_code}
              helperText={formik.errors.zip_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </Grid>
        <div id="checkout" />
        <Grid container alignItems="center">
          <Grid item xs={6} className="pr-3">
            <CustomButton color="background" onClick={handleCancel} fullWidth>
              Anterior
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton
              id="submit"
              type="submit"
              variant="solid"
              color="primary"
              onClick={handleSubmit}
              disabled={processing || disabled}
              className="my-5 py-3"
              fullWidth
            >
              <Lock className="mr-2" />
              {processing || disabled ? <CircularProgress color="white" size={24} /> : 'Pagar'}
            </CustomButton>
          </Grid>
        </Grid>
        <p id="cardTokenId" />
        <div id="3ds-iframe-container" />
      </form>
    </div>
  );
};

export default ClipForm;
